import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { TextField, InputAdornment } from '@mui/material';

/**
 * Comma-formatted, "$"-prefixed whole-dollar input. Shared by the calculators so
 * every money field behaves the same (caret-preserving reformat, blank at zero).
 */
const CurrencyTextField: React.FC<{
  value: number;
  onChange: (v: number) => void;
  highlightColor: string;
  /** When set, the value is clamped up to this minimum on blur (typing stays free). */
  minValue?: number;
}> = ({ value, onChange, highlightColor, minValue }) => {
  const fmt = (n: number) => n === 0 ? '' : n.toLocaleString('en-SG');
  const [display, setDisplay] = useState(() => fmt(value));
  const inputRef = useRef<HTMLInputElement>(null);
  const lastKey = useRef('');
  // Digits left of the caret at the last keystroke. The comma reformat replaces the
  // whole input value, which throws the caret to the end — the layout effect below
  // uses this count to put it back where the user was typing.
  const caretDigits = useRef<number | null>(null);

  useEffect(() => { setDisplay(fmt(value)); }, [value]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (caretDigits.current === null || !el || document.activeElement !== el) {
      caretDigits.current = null;
      return;
    }
    let pos = 0;
    let seen = 0;
    while (pos < display.length && seen < caretDigits.current) {
      if (display[pos] >= '0' && display[pos] <= '9') seen++;
      pos++;
    }
    el.setSelectionRange(pos, pos);
    caretDigits.current = null;
  }, [display]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    let digits = raw.replace(/[^0-9]/g, '');
    let digitsLeft = raw.slice(0, caret).replace(/[^0-9]/g, '').length;
    // Deleting a comma removes no digit, so the value would just reformat back
    // unchanged — delete the adjacent digit instead, matching the user's intent.
    if (digits === display.replace(/[^0-9]/g, '') && raw.length < display.length) {
      if (lastKey.current === 'Backspace' && digitsLeft > 0) {
        digits = digits.slice(0, digitsLeft - 1) + digits.slice(digitsLeft);
        digitsLeft -= 1;
      } else if (lastKey.current === 'Delete') {
        digits = digits.slice(0, digitsLeft) + digits.slice(digitsLeft + 1);
      }
    }
    const n = digits === '' ? 0 : parseInt(digits, 10);
    caretDigits.current = digitsLeft;
    setDisplay(digits === '' ? '' : n.toLocaleString('en-SG'));
    onChange(n);
  };

  return (
    <TextField
      size="small"
      fullWidth
      value={display}
      placeholder="0"
      inputRef={inputRef}
      inputProps={{ inputMode: 'numeric' }}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      onChange={handleChange}
      onKeyDown={(e) => { lastKey.current = e.key; if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      onBlur={() => {
        if (minValue != null && value < minValue) {
          onChange(minValue);
          setDisplay(fmt(minValue));
        }
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': { borderColor: highlightColor },
        },
        '& input': { fontWeight: 600, textAlign: 'right' },
      }}
    />
  );
};

export default CurrencyTextField;
